import React, { useState } from 'react';
import { Combobox } from '@/components/ui/combobox';
import type { AgentFormRequest, AgentFormField } from '@/core/ai/agent/IAgentTool';

interface FormInputCardProps {
  formRequest: AgentFormRequest;
  isActive: boolean;
  onSubmit: (formattedMessage: string, rawValues: Record<string, string>) => void;
}

export const FormInputCard: React.FC<FormInputCardProps> = ({ formRequest, isActive, onSubmit }) => {
  const [values, setValues] = useState<Record<string, string>>({});

  const setValue = (name: string, value: string) =>
    setValues((prev) => ({ ...prev, [name]: value }));

  const allRequiredFilled = formRequest.fields
    .filter((f) => f.required !== false)
    .every((f) => values[f.name]?.trim());

  const handleSubmit = () => {
    if (!allRequiredFilled || !isActive) return;
    const lines = formRequest.fields.map((field) => {
      const raw = values[field.name] ?? '';
      if (field.type === 'person' && field.options) {
        const opt = field.options.find((o) => o.value === raw);
        return opt ? `${field.label}: ${opt.label} [id: ${opt.value}]` : `${field.label}: ${raw}`;
      }
      return `${field.label}: ${raw}`;
    });
    onSubmit(lines.join('\n'), { ...values });
  };

  if (!isActive) {
    return (
      <div className="text-xs text-muted-foreground flex items-center gap-1">
        <span className="text-emerald-600">✓</span>
        <span>Form submitted.</span>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-border bg-background p-3 space-y-3">
      <div className="text-xs font-medium text-foreground">{formRequest.title}</div>
      {formRequest.fields.map((field) => (
        <FieldRenderer
          key={field.name}
          field={field}
          value={values[field.name] ?? ''}
          onChange={(v) => setValue(field.name, v)}
        />
      ))}
      <button
        onClick={handleSubmit}
        disabled={!allRequiredFilled}
        className="w-full rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-40 hover:bg-primary/90 transition-colors"
      >
        Continue
      </button>
    </div>
  );
};

const FieldRenderer: React.FC<{
  field: AgentFormField;
  value: string;
  onChange: (v: string) => void;
}> = ({ field, value, onChange }) => (
  <div className="space-y-1">
    <label className="text-xs text-muted-foreground">
      {field.label}
      {field.required !== false && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
    {(field.type === 'person' || field.type === 'select') && field.options ? (
      <Combobox
        options={field.options}
        value={value}
        onValueChange={onChange}
        placeholder={field.placeholder ?? `Select ${field.label.toLowerCase()}…`}
        searchPlaceholder="Search…"
      />
    ) : (
      <input
        type={field.type === 'number' ? 'number' : 'text'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder ?? ''}
        className="w-full h-8 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
      />
    )}
  </div>
);
